import { FC } from "react";
import styled from "styled-components";

import Text from "@/components/Text";

const Box = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  padding-right: 20px;
  display: flex;
  align-items: center;
  margin-left: 16px;
  &:last-of-type {
    border-right: 0;
  }
  &:first-of-type {
    margin-left: 0;
  }
`;

const Placeholder = styled.div`
  background: linear-gradient(180deg, #f2f3f7 0%, #ebedf3 100%);
  border-radius: 8px;
  width: 64px;
  height: 64px;
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImagesCount = styled(Text)`
  background: ${({ theme }) => theme.colors.purple};
  border-radius: 9px;
  height: 18px;
  display: flex;
  align-items: center;
  margin-left: 8px;
  padding: 0 8px;
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const ActionsHolder = styled.div`
  display: flex;
`;

const DownloadAll = styled(Text)`
  padding-right: 12px;
  border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
  transition: all ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const UnselectAll = styled(Text)`
  margin-left: 12px;
  transition: all ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

interface Props {
  type: "library" | "midjourney" | "instagram";
  selectedImages: number[];
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
  title: string;
}

const ImageToolSummaryBox: FC<Props> = ({
  type,
  selectedImages,
  setSelectedImages,
  title,
}) => {
  const renderIcon = () => {
    switch (type) {
      case "library":
        return (
          <svg
            width="28"
            height="24"
            viewBox="0 0 28 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M24.6083 2H3.39397C2.86141 2 2.42969 2.44772 2.42969 3V21C2.42969 21.5523 2.86141 22 3.39397 22H24.6083C25.1408 22 25.5725 21.5523 25.5725 21V3C25.5725 2.44772 25.1408 2 24.6083 2Z"
              fill="#EFF1F6"
              stroke="#D3D6DF"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8.49684 10.706L2.42871 16.9989L3.5 17.0914V21H24.5V18.9063L25.5715 18.9989L20.4677 13.706C20.3781 13.6131 20.2718 13.5395 20.1549 13.4892C20.0379 13.439 19.9125 13.4131 19.7858 13.4131C19.6592 13.4131 19.5338 13.439 19.4168 13.4892C19.2998 13.5395 19.1935 13.6131 19.104 13.706L16.6105 16.2918C16.521 16.3846 16.4147 16.4583 16.2977 16.5085C16.1807 16.5588 16.0553 16.5847 15.9287 16.5847C15.8021 16.5847 15.6767 16.5588 15.5597 16.5085C15.4427 16.4583 15.3364 16.3846 15.2468 16.2918L9.86055 10.706C9.771 10.6131 9.6647 10.5395 9.54771 10.4892C9.43072 10.439 9.30533 10.4131 9.17869 10.4131C9.05206 10.4131 8.92667 10.439 8.80968 10.4892C8.69269 10.5395 8.58638 10.6131 8.49684 10.706Z"
              fill="#D3D6DF"
            />
            <path
              d="M17.75 10.5C18.9926 10.5 20 9.49264 20 8.25C20 7.00736 18.9926 6 17.75 6C16.5074 6 15.5 7.00736 15.5 8.25C15.5 9.49264 16.5074 10.5 17.75 10.5Z"
              fill="#D3D6DF"
            />
          </svg>
        );
      case "midjourney":
        return (
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 22V0H3.72778L11 14.6056L18.2722 0H22V22H18.1194V8.86111L11.7333 22H10.2667L3.88056 8.86111V22H0Z"
              fill="#D3D6DF"
            />
          </svg>
        );
      case "instagram":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19.9229 1.61523H8.07677C4.50816 1.61523 1.61523 4.50816 1.61523 8.07677V19.9229C1.61523 23.4915 4.50816 26.3845 8.07677 26.3845H19.9229C23.4915 26.3845 26.3845 23.4915 26.3845 19.9229V8.07677C26.3845 4.50816 23.4915 1.61523 19.9229 1.61523Z"
              fill="#D3D6DF"
              stroke="#D3D6DF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.9999 19.3845C16.9737 19.3845 19.3845 16.9737 19.3845 13.9999C19.3845 11.026 16.9737 8.61523 13.9999 8.61523C11.026 8.61523 8.61523 11.026 8.61523 13.9999C8.61523 16.9737 11.026 19.3845 13.9999 19.3845Z"
              stroke="#EFF0F6"
              strokeWidth="2"
              strokeMiterlimit="10"
            />
            <path
              d="M21.0002 8.61553C21.8923 8.61553 22.6155 7.8923 22.6155 7.00015C22.6155 6.108 21.8923 5.38477 21.0002 5.38477C20.108 5.38477 19.3848 6.108 19.3848 7.00015C19.3848 7.8923 20.108 8.61553 21.0002 8.61553Z"
              fill="#F0F1F6"
            />
          </svg>
        );
    }
  };
  return (
    <Box>
      <Placeholder>{renderIcon()}</Placeholder>
      <div>
        <Heading $type="button">
          {title}
          <ImagesCount as="span" $type="extraSmall" $color="white">
            {selectedImages.length ?? 0}
          </ImagesCount>
        </Heading>
        <ActionsHolder>
          <DownloadAll
            href={`${
              process.env.REACT_APP_API_ENDPOINT
            }/image-library-zip/?media=${selectedImages.map(
              (selectedImage) => selectedImage
            )}`}
            $color="gray"
            $type="smallHeadline"
            as="a"
          >
            Download selected
          </DownloadAll>
          <UnselectAll
            onClick={() => setSelectedImages([])}
            $color="gray"
            $type="smallHeadline"
            as="button"
          >
            Unselect all
          </UnselectAll>
        </ActionsHolder>
      </div>
    </Box>
  );
};

export default ImageToolSummaryBox;
